<template>
  <k-panel
    title="Project Competencies"
    :hasContentToggle="false"
    >
    <template #body>
      <div class="competencies-content">
        <div class="competencies-layout" :class="{ 'with-table': competencyDescriptionsVisible }">
          <div class="competencies-left">
            <div class="competencies-info">
              <p>Ensure all listed competencies are met by the submitted evidence.</p>
              <p>
                Competencies met by evidence approved by your mentor are listed as approved.
                <a href="#"
                class="competency-descriptions-link"
                @click.prevent="$emit('toggle-descriptions')">
                Competency descriptions
                <i :class="chevronClass"></i>
                </a>
              </p>
            </div>

            <div class="competencies-badges">
              <div class="competency-pills">
                <k-status-pill
                  v-for="competency in visibleCompetencies"
                  :key="competency.id"
                  :type="competency.approved ? 'success' : 'neutral'"
                  :status-value="competency.label"
                />
              </div>
              <div v-if="shouldShowToggleButton" class="toggle-container">
                <button
                  class="show-more-btn"
                  @click="toggleShowAll"
                  >
                  {{ showAll ? 'Show less' : `+ ${hiddenCompetencies.length} more` }}
                  <i :class="showAll ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="competencyDescriptionsVisible" class="competencies-right">
            <div class="competency-table-container">
              <k-table
                :headers="competencyTableHeaders"
                :rows="competencyTableRows"
                :hideable="false"
                :downloadable="false"
                :panel="false"
                :max="5"
                class="competency-descriptions-table"
              />
              </div>
          </div>
        </div>
      </div>
    </template>
  </k-panel>
</template>

<style scoped>
.competencies-content {
  padding: 15px 0;
}

.competencies-layout {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.competencies-left {
  flex: 1;
  min-width: 0;
}

.competencies-right {
  flex: 1;
  min-width: 400px;
}

.competencies-info {
  margin-bottom: 25px;
}

.competencies-info p {
  margin-bottom: 10px;
  color: var(--kate-type-primary);
}

.competency-descriptions-link {
  color: var(--kate-type-link);
  text-decoration: none;
  cursor: pointer;
}

.competency-descriptions-link:hover {
  text-decoration: underline;
}

.competency-descriptions-link i {
  margin-left: 5px;
  font-size: 0.8em;
}

.competencies-badges {
  margin-top: 20px;
}

.competency-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.show-more-btn {
  background: none;
  border: none;
  color: var(--kate-type-link);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.show-more-btn:hover {
  background-color: var(--kate-background-alt);
  text-decoration: underline;
}

.show-more-btn i {
  font-size: 12px;
}

@media (max-width: 768px) {
  .competencies-layout {
    flex-direction: column;
    gap: 20px;
  }

  .competencies-right {
    min-width: 0;
    max-width: 100%;
  }

  .competency-pills {
    grid-template-columns: repeat(auto-fit, minmax(60px, max-content));
    gap: 8px;
  }
}

@media (max-width: 1200px) {
  .competencies-layout.with-table {
    gap: 15px;
  }

  .competencies-layout.with-table .competency-pills {
    grid-template-columns: repeat(auto-fit, minmax(60px, max-content));
    gap: 6px;
  }
}
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import KStatusPill from '@base-components/k-status-pill.vue';

export default {
  name: 'ProjectEvidenceCompetencies',
  components: {
    KPanel,
    KTable,
    KStatusPill,
  },
  props: {
    competencies: {
      type: Array,
      required: true,
      default: () => [],
    },
    competencyDescriptionsVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAll: false,
    };
  },

  computed: {
    visibleCompetencies() {
      const maxVisible = this.competencyDescriptionsVisible ? 8 : 10;
      return this.showAll ? this.competencies : this.competencies.slice(0, maxVisible);
    },
    hiddenCompetencies() {
      const maxVisible = this.competencyDescriptionsVisible ? 8 : 10;
      return this.showAll ? [] : this.competencies.slice(maxVisible);
    },
    shouldShowToggleButton() {
      const maxVisible = this.competencyDescriptionsVisible ? 8 : 10;
      return this.competencies.length > maxVisible;
    },
    chevronClass() {
      return this.competencyDescriptionsVisible
        ? 'fas fa-angle-up'
        : 'fas fa-angle-down';
    },
    competencyTableHeaders() {
      return {
        label: {
          name: 'Label',
          type: 'pill',
          sortable: true,
          filterable: true,
          options: {
            type: 'neutral',
          },
        },
        category: {
          name: 'Category',
          type: 'string',
          sortable: true,
          hasDropdown: true,
        },
        description: {
          name: 'Description',
          type: 'string',
          sortable: false,
          filterable: true,
        },
      };
    },
    competencyTableRows() {
      return this.competencies.map(competency => ({
        label: competency.label,
        category: competency.category,
        description: competency.description,
      }));
    },
  },

  methods: {
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
  },
};
</script>
